import { useMemo, useState } from "react";
import {
  IconAlertTriangle,
  IconCircleCheck,
  IconChevronDown,
  IconChevronUp,
  IconFileAnalytics,
  IconLayoutList,
  IconLock,
  IconScale,
} from "@tabler/icons-react";
import { Trans, msg } from "@lingui/macro";
import { useLingui } from "@lingui/react";

import { analytics } from "../../analytics/analyticsEvents";
import { getContractById, skills as allSkills, tools as allTools } from "../../content";
import { useLocalize } from "../../content/useLocalize";
import { suitableSkillsForType, suitableToolsForType } from "../../engine/loadout";
import { calculateReportScore, getExpectedFindings } from "../../engine/reportScore";
import { budgetZone } from "../../engine/scoring";
import { severityText } from "../../i18n/enumText";
import { costTooltip, toneTooltip } from "../../i18n/tooltips";
import { useGameStore } from "../../state/store";
import type { RecommendedActionCode } from "../../types";
import { Button } from "../common/Button";
import { Tooltip } from "../common/Tooltip";
import { EmptyState } from "../common/EmptyState";
import { IconButton } from "../common/IconButton";
import { Panel } from "../common/Panel";
import { ProgressBar } from "../common/ProgressBar";
import { RecommendedActionSelect } from "../common/RecommendedActionSelect";
import { RiskBadge } from "../common/RiskBadge";
import { cx } from "../common/cx";
import { formatMoney } from "../common/format";
import { useGameplaySession } from "../common/useGameplaySession";
import { LoadoutPicker } from "../loadout/LoadoutPicker";
import { useLoadoutNames } from "../loadout/useLoadoutNames";
import styles from "./ReportBuilder.module.css";

const measureLabel = msg`Рекомендация`;
const moveUpLabel = msg`Выше`;
const moveDownLabel = msg`Ниже`;

const PREFILL_SEVERITIES: ReadonlySet<string> = new Set(["high", "critical"]);
const BUDGET_TOLERANCE = 0.2;

export function ReportBuilder(): JSX.Element {
  const { i18n } = useLingui();
  const t = useLocalize();
  const { toolName, skillName } = useLoadoutNames();
  const activeContractId = useGameStore((state) => state.activeContractId);
  const activeToolIds = useGameStore((state) => state.activeToolIds);
  const activeSkillIds = useGameStore((state) => state.activeSkillIds);
  const player = useGameStore((state) => state.player);
  const toggleActiveTool = useGameStore((state) => state.toggleActiveTool);
  const toggleActiveSkill = useGameStore((state) => state.toggleActiveSkill);
  const reportingLevel = useGameStore((state) =>
    state.activeSkillIds.includes("reporting") ? (state.player.skills.reporting ?? 0) : 0,
  );
  const navigateTo = useGameStore((state) => state.navigateTo);
  const completeTask = useGameStore((state) => state.completeTask);

  const contract = activeContractId ? getContractById(activeContractId) : undefined;
  const findings = contract?.findings ?? [];
  const clientBudget = contract?.clientBudget ?? 0;
  const byId = useMemo(() => new Map(findings.map((f) => [f.id, f])), [findings]);

  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [recommendations, setRecommendations] = useState<Record<string, RecommendedActionCode>>({});
  const [submitted, setSubmitted] = useState(false);
  const [score, setScore] = useState(0);

  useGameplaySession("report_builder");

  if (!contract || findings.length === 0) {
    return (
      <EmptyState
        title={<Trans>Нет находок для отчёта</Trans>}
        action={
          <Button variant="secondary" onClick={() => navigateTo("workspace")}>
            <Trans>Назад</Trans>
          </Button>
        }
      />
    );
  }

  const used = selectedIds.reduce((sum, id) => sum + (byId.get(id)?.cost ?? 0), 0);
  const budgetTolerance = reportingLevel >= 3 ? BUDGET_TOLERANCE : 0;
  const toleratedBudget = Math.round(clientBudget * (1 + budgetTolerance));
  const budgetContext = budgetZone(used, clientBudget);
  const overBudget = used > toleratedBudget;
  const withinTolerance = used > clientBudget && used <= toleratedBudget;
  const overAmountLabel = formatMoney(used - clientBudget);
  const budgetPct = clientBudget > 0 ? Math.round((used / clientBudget) * 100) : 0;
  const tolerancePct = Math.round(BUDGET_TOLERANCE * 100);

  const toggle = (id: string): void => {
    if (submitted) {
      return;
    }
    const adding = !selectedIds.includes(id);
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((entry) => entry !== id) : [...prev, id],
    );
    const finding = byId.get(id);
    if (adding && finding && reportingLevel >= 1 && PREFILL_SEVERITIES.has(finding.severity)) {
      setRecommendations((prev) =>
        prev[id] ? prev : { ...prev, [id]: finding.recommendedActionCode },
      );
    }
  };

  const move = (index: number, delta: number): void => {
    setSelectedIds((prev) => {
      const target = index + delta;
      if (target < 0 || target >= prev.length) {
        return prev;
      }
      const next = [...prev];
      [next[index], next[target]] = [next[target]!, next[index]!];
      return next;
    });
  };

  const pickReco = (id: string, code: RecommendedActionCode): void => {
    setRecommendations((prev) => ({ ...prev, [id]: code }));
  };

  const submit = (): void => {
    const result = calculateReportScore({
      findings,
      clientBudget,
      selectedFindingIds: selectedIds,
      chosenRecommendations: recommendations,
      hasReportTemplate: activeToolIds.includes("report_template"),
      hasPriorityPlanner: activeToolIds.includes("priority_planner"),
      budgetTolerance,
    });
    setScore(result);
    setSubmitted(true);
    analytics.taskSubmitted({ type: "report_builder", score: result });
  };

  const finish = (): void => {
    completeTask(score);
    navigateTo("workspace");
  };

  const expectedIds = useMemo(
    () => new Set(getExpectedFindings(findings, clientBudget).map((f) => f.id)),
    [findings, clientBudget],
  );

  const briefHinted =
    !submitted && activeToolIds.includes("client_brief_analyzer") ? expectedIds : null;

  const showRecoTemplateHint = !submitted && reportingLevel >= 1;
  const showPriorityOrderHint = !submitted && reportingLevel >= 2;

  const pickerTools = suitableToolsForType("report_builder", player.ownedTools, allTools);
  const pickerSkills = suitableSkillsForType("report_builder", player.skills, allSkills);

  const measuresAssigned = selectedIds.filter((id) => recommendations[id] !== undefined).length;
  const hasSelection = selectedIds.length > 0;
  const reportSections = [
    hasSelection,
    hasSelection && measuresAssigned === selectedIds.length,
    hasSelection,
    hasSelection && !overBudget,
  ];
  const sectionsFilled = reportSections.filter(Boolean).length;
  const readiness = Math.round((sectionsFilled / reportSections.length) * 100);
  const readinessContext = readiness >= 75 ? "accent" : readiness >= 50 ? "medium" : "critical";
  const readinessToneClass =
    readiness >= 75 ? styles.toneAccent : readiness >= 50 ? styles.toneMedium : styles.toneCritical;

  return (
    <div className={styles.screen}>
      <LoadoutPicker
          tools={pickerTools}
          skills={pickerSkills}
          skillLevels={player.skills}
          activeToolIds={activeToolIds}
          activeSkillIds={activeSkillIds}
          readOnly={submitted}
          onToggleTool={toggleActiveTool}
          onToggleSkill={toggleActiveSkill}
        />
      <div className={styles.layout}>
        <div className={styles.main}>
      <Panel
        title={<Trans>Сборка отчёта</Trans>}
        actions={
          submitted ? (
            <span className={cx(styles.scoreTag, "mono")}>
              <Trans>Очки: {score}</Trans>
            </span>
          ) : undefined
        }
      >
        <p className={styles.hint}>
          <Trans>
            Бери самые значимые находки в рамках бюджета. Верная мера и порядок повышают балл.
            Оценка - за покрытие важного, верные меры, порядок и бюджет.
          </Trans>
        </p>

        {briefHinted && briefHinted.size > 0 && (
          <div className={styles.skillHint}>
            <IconLayoutList size={14} aria-hidden="true" />
            {toolName("client_brief_analyzer")}:{" "}
            <Trans>подсвечены находки, которые клиент ждёт в отчёте</Trans>
          </div>
        )}
        {showRecoTemplateHint && (
          <div className={styles.skillHint}>
            <IconLayoutList size={14} aria-hidden="true" />
            {skillName("reporting")}:{" "}
            <Trans>у находок высокой значимости мера подставлена автоматически, проверь её</Trans>
          </div>
        )}

        <div className={styles.regionHead}>
          <span className={styles.regionTitle}>
            <Trans>1. Подтверди выводы и подбери меры</Trans>
          </span>
          <span className={styles.regionHint}>
            <Trans>Отмечай то, что реально нашёл. У отмеченной находки выбери меру.</Trans>
          </span>
        </div>
        <ul className={styles.findings} data-tutorial="findings">
          {findings.map((finding) => {
            const checked = selectedIds.includes(finding.id);
            const expectedHit = submitted && expectedIds.has(finding.id);
            const briefHit = briefHinted?.has(finding.id) ?? false;
            return (
              <li
                key={finding.id}
                className={cx(styles.finding, checked && styles.checked, (expectedHit || briefHit) && styles.expected)}
              >
                <label className={styles.findingMain}>
                  <input
                    type="checkbox"
                    checked={checked}
                    disabled={submitted}
                    onChange={() => toggle(finding.id)}
                  />
                  <span className={styles.findingTitle}>{t(finding.title)}</span>
                </label>
                <div className={styles.findingMeta}>
                  <RiskBadge
                    tone={finding.severity}
                    label={severityText(finding.severity)}
                    tooltip={i18n._(toneTooltip(finding.severity))}
                    tooltipTabIndex={-1}
                  />
                  <Tooltip content={i18n._(costTooltip())}>
                    <span className={cx(styles.cost, "mono")}>{formatMoney(finding.cost)}</span>
                  </Tooltip>
                </div>
                {checked && (
                  <RecommendedActionSelect
                    className={styles.select}
                    value={recommendations[finding.id] ?? ""}
                    ariaLabel={measureLabel}
                    disabled={submitted}
                    onChange={(code) => pickReco(finding.id, code)}
                  />
                )}
              </li>
            );
          })}
        </ul>

        <div className={styles.budget}>
          <div className={styles.regionHead}>
            <span className={styles.regionTitle}>
              <Trans>2. Уложись в бюджет</Trans>
            </span>
          </div>
          <div className={styles.budgetMeta}>
            <span>
              <Trans>Бюджет клиента</Trans>
            </span>
            <span className={cx("mono", overBudget && styles.over)}>
              {formatMoney(used)} / {formatMoney(clientBudget)}
            </span>
          </div>
          <ProgressBar value={used} max={clientBudget || 1} context={budgetContext} label={i18n._(msg`Бюджет`)} />
          <div className={cx(styles.budgetStatus, overBudget ? styles.budgetOver : styles.budgetOk)}>
            {overBudget ? (
              <IconAlertTriangle size={14} aria-hidden="true" />
            ) : (
              <IconCircleCheck size={14} aria-hidden="true" />
            )}
            <span>
              {overBudget ? (
                <Trans>Превышение на {overAmountLabel}</Trans>
              ) : withinTolerance ? (
                <Trans>Превышение на {overAmountLabel} в пределах допуска клиента</Trans>
              ) : (
                <Trans>В рамках бюджета</Trans>
              )}
            </span>
            <span className={cx(styles.budgetPct, "mono")}>{budgetPct}%</span>
          </div>
          {!submitted && reportingLevel >= 3 && (
            <div className={styles.skillHint}>
              <IconScale size={14} aria-hidden="true" />
              {skillName("reporting")}: <Trans>клиент прощает превышение бюджета до {tolerancePct}%</Trans>
            </div>
          )}
        </div>

        {selectedIds.length > 0 && (
          <div className={styles.report}>
            <div className={styles.regionHead}>
              <span className={styles.regionTitle}>
                <Trans>3. Расставь приоритеты</Trans>
              </span>
              <span className={styles.regionHint}>
                <Trans>Что клиенту сделать в первую очередь? Порядок влияет на оценку.</Trans>
              </span>
            </div>
            <ol className={styles.reportList}>
              {selectedIds.map((id, index) => {
                const finding = byId.get(id);
                if (!finding) {
                  return null;
                }
                const priorityHinted = showPriorityOrderHint && expectedIds.has(id);
                return (
                  <li key={id} className={cx(styles.reportItem, priorityHinted && styles.priorityHinted)}>
                    <span className={cx(styles.reportNum, "mono")}>{index + 1}</span>
                    <span className={styles.reportTitle}>{t(finding.title)}</span>
                    {!submitted && (
                      <span className={styles.reorder}>
                        <IconButton
                          label={i18n._(moveUpLabel)}
                          icon={<IconChevronUp size={16} />}
                          onClick={() => move(index, -1)}
                          disabled={index === 0}
                        />
                        <IconButton
                          label={i18n._(moveDownLabel)}
                          icon={<IconChevronDown size={16} />}
                          onClick={() => move(index, 1)}
                          disabled={index === selectedIds.length - 1}
                        />
                      </span>
                    )}
                  </li>
                );
              })}
            </ol>
          </div>
        )}

        <div className={styles.footer}>
          {submitted ? (
            <span data-tutorial="task-continue">
              <Button variant="primary" onClick={finish}>
                <Trans>Продолжить</Trans>
              </Button>
            </span>
          ) : (
            <Button variant="primary" onClick={submit} disabled={selectedIds.length === 0}>
              <Trans>Отправить отчёт</Trans>
            </Button>
          )}
        </div>
      </Panel>
        </div>

        <aside className={styles.side}>
          <Panel title={<Trans>Предпросмотр отчёта</Trans>} icon={<IconFileAnalytics size={17} />}>
            <div className={styles.previewRows}>
              <div className={styles.previewRow}>
                <span className={styles.previewKey}>
                  <Trans>Выводов</Trans>
                </span>
                <span className={cx(styles.previewVal, "mono")}>{selectedIds.length}</span>
              </div>
              <div className={styles.previewRow}>
                <span className={styles.previewKey}>
                  <Trans>Мер назначено</Trans>
                </span>
                <span className={cx(styles.previewVal, "mono")}>
                  {measuresAssigned} / {selectedIds.length}
                </span>
              </div>
              <div className={styles.previewRow}>
                <span className={styles.previewKey}>
                  <Trans>Бюджет</Trans>
                </span>
                <span className={cx(styles.previewVal, "mono", overBudget && styles.previewOver)}>
                  {formatMoney(used)} / {formatMoney(clientBudget)}
                </span>
              </div>
            </div>

            <div className={styles.readiness}>
              <div className={styles.readinessTop}>
                <span className={styles.readinessLabel}>
                  <Trans>Готовность отчёта</Trans>
                </span>
                <span className={cx(styles.readinessVal, "mono", readinessToneClass)}>{readiness}%</span>
              </div>
              <ProgressBar value={readiness} max={100} context={readinessContext} label={i18n._(msg`Готовность отчёта`)} />
            </div>

            <p className={styles.weightsNote}>
              <IconScale size={14} aria-hidden="true" />
              <Trans>Оценка: выводы 40%, меры 30%, приоритеты 20%, бюджет 10%.</Trans>
            </p>

            {submitted && (
              <div className={styles.lockedNote}>
                <IconLock size={14} aria-hidden="true" />
                <Trans>Отчёт отправлен - изменить нельзя</Trans>
              </div>
            )}
          </Panel>
        </aside>
      </div>
    </div>
  );
}
